import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CalendarClock, Download, Eye, FilePenLine, FileText, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, unwrap } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/i18n";
import { useLookupValues } from "@/lib/platformConfig";
import { formatDate } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

const emptyForm = () => ({ kind: "", number: "", issue_date: "", expiry_date: "", notes: "", file: null });

const fileIdsFor = (doc) => (Array.isArray(doc?.file_ids) ? doc.file_ids.filter(Boolean) : (doc?.file_id ? [doc.file_id] : []));

const fileNamesFor = (doc) => (Array.isArray(doc?.file_names) ? doc.file_names.filter(Boolean) : (doc?.file_name ? [doc.file_name] : []));

export function inspectionExpiryState(expiryDate, t, todayValue = new Date()) {
  if (!expiryDate) return { label: t("management.inspectionNoDate"), className: "bg-[var(--subtle)] text-[var(--text-secondary)] border-0" };
  const today = new Date(todayValue);
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${expiryDate}T00:00:00`);
  const days = Math.ceil((target - today) / 86400000);
  if (days < 0) return { label: t("nursery.expired"), className: "bg-[var(--error-bg)] text-[var(--error)] border-0" };
  if (days <= 30) return { label: t("nursery.d30"), className: "bg-[var(--error-bg)] text-[var(--error)] border-0" };
  if (days <= 60) return { label: t("nursery.d60"), className: "bg-[var(--warning-bg)] text-[var(--warning)] border-0" };
  if (days <= 90) return { label: t("nursery.d90"), className: "bg-[var(--info-bg)] text-[var(--info)] border-0" };
  return { label: t("management.inspectionCurrent"), className: "bg-[var(--success-bg)] text-[var(--success)] border-0" };
}

export default function InspectionDocuments() {
  const { t, language } = useI18n();
  const { user } = useAuth();
  const institution = user?.institution;
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const lookup = useLookupValues("institution_document_kinds", { enabled: !!institution?.id });
  const documentKinds = useMemo(
    () => (lookup.data?.values || []).filter((row) => row.active !== false),
    [lookup.data?.values],
  );
  const kindByValue = useMemo(
    () => Object.fromEntries(documentKinds.map((row) => [row.value, row])),
    [documentKinds],
  );
  const kindLabel = (kind) => (language === "ar" && kind?.metadata?.label_ar) || kind?.label;
  const selectedKind = kindByValue[form.kind];
  const isCheque = selectedKind?.metadata?.date_label === "due_date";

  useEffect(() => {
    if (!documentKinds.length || editing) return;
    setForm((current) => (documentKinds.some((row) => row.value === current.kind)
      ? current
      : { ...current, kind: documentKinds[0].value }));
  }, [documentKinds, editing]);

  const { data: documents = [], isLoading, isError, refetch } = useQuery({
    queryKey: ["inspection-documents", institution?.id],
    queryFn: async () => unwrap(await api.get("/documents", { params: { subject_type: "institution", subject_id: institution.id } })),
    enabled: !!institution?.id,
  });

  const resetDialog = (open) => {
    setDialogOpen(open);
    if (!open) {
      setEditing(null);
      setForm({ ...emptyForm(), kind: documentKinds[0]?.value || "" });
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm(), kind: documentKinds[0]?.value || "" });
    setDialogOpen(true);
  };

  const openEdit = (doc) => {
    setEditing(doc);
    setForm({
      kind: doc.kind || "",
      number: doc.number || "",
      issue_date: doc.issue_date || "",
      expiry_date: doc.expiry_date || "",
      notes: doc.notes || "",
      file: null,
    });
    setDialogOpen(true);
  };

  const uploadFile = async (file) => {
    const data = new FormData();
    data.append("file", file);
    return unwrap(await api.post("/files/upload", data, { headers: { "Content-Type": "multipart/form-data" } }));
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (selectedKind?.metadata?.requires_expiry && !form.expiry_date) {
        throw new Error(isCheque ? t("management.inspectionChequeDateRequired") : t("management.inspectionExpiryRequired"));
      }
      const payload = {
        kind: form.kind,
        number: form.number.trim() || null,
        issue_date: form.issue_date || null,
        expiry_date: form.expiry_date || null,
        notes: form.notes.trim() || null,
      };
      if (form.file) {
        const uploaded = await uploadFile(form.file);
        payload.file_id = uploaded.id;
      }
      if (editing) return unwrap(await api.put(`/documents/${editing.id}`, payload));
      return unwrap(await api.post("/documents", { ...payload, subject_type: "institution", subject_id: institution.id }));
    },
    onSuccess: () => {
      toast.success(editing ? t("management.inspectionUpdated") : t("management.inspectionCreated"));
      qc.invalidateQueries({ queryKey: ["inspection-documents", institution?.id] });
      qc.invalidateQueries({ queryKey: ["institution-documents", institution?.id] });
      qc.invalidateQueries({ queryKey: ["documents-search"] });
      qc.invalidateQueries({ queryKey: ["docs-expiring"] });
      resetDialog(false);
    },
    onError: (error) => toast.error(error?.response ? errMsg(error) : error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => unwrap(await api.delete(`/documents/${id}`)),
    onSuccess: () => {
      toast.success(t("management.inspectionDeleted"));
      qc.invalidateQueries({ queryKey: ["inspection-documents", institution?.id] });
      qc.invalidateQueries({ queryKey: ["institution-documents", institution?.id] });
      qc.invalidateQueries({ queryKey: ["documents-search"] });
      qc.invalidateQueries({ queryKey: ["docs-expiring"] });
    },
    onError: (error) => toast.error(errMsg(error)),
  });

  const getFile = async (doc, fileIndex, download) => {
    try {
      const response = await api.get(`/documents/${doc.id}/download`, { params: { file_index: fileIndex }, responseType: "blob" });
      const blob = new Blob([response.data], { type: response.headers["content-type"] || "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      if (download) {
        const anchor = window.document.createElement("a");
        anchor.href = url;
        anchor.download = fileNamesFor(doc)[fileIndex] || `${doc.kind}.bin`;
        window.document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
        window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      }
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  if (!institution?.id) {
    return <Card><CardContent className="p-6 text-sm text-[var(--text-muted)]">{t("management.inspectionNoInstitution")}</CardContent></Card>;
  }

  return (
    <div className="space-y-5" data-testid="inspection-documents-section">
      <Card className="border-[var(--subtle)] overflow-hidden">
        <CardHeader className="bg-[var(--surface)] border-b border-[var(--subtle)] md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <CardTitle className="font-display flex items-center gap-2">
              <FileText className="h-5 w-5 text-[var(--accent)]" />
              {t("management.inspectionTitle")}
            </CardTitle>
            <p className="text-sm text-[var(--text-muted)] mt-1">{t("management.inspectionSubtitle")}</p>
          </div>
          <Button type="button" onClick={openCreate} disabled={!documentKinds.length} data-testid="inspection-add-document" className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white active:scale-95 transition-transform">
            <Plus className="h-4 w-4 me-2" />{t("management.inspectionAdd")}
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading || lookup.isLoading ? (
            <div className="p-5" data-testid="inspection-documents-loading"><Skeleton className="h-40" /></div>
          ) : isError || lookup.isError ? (
            <div className="p-8 text-center" data-testid="inspection-documents-error">
              <AlertTriangle className="h-7 w-7 text-[var(--error)] mx-auto mb-2" />
              <p className="text-sm text-[var(--text-secondary)]">{t("management.inspectionLoadError")}</p>
              <Button type="button" variant="outline" className="mt-3" onClick={() => refetch()} data-testid="inspection-retry">{t("common.retry")}</Button>
            </div>
          ) : !documentKinds.length ? (
            <p className="p-8 text-center text-sm text-[var(--text-muted)]" data-testid="inspection-no-types">{t("management.inspectionNoTypes")}</p>
          ) : !documents.length ? (
            <div className="p-10 text-center" data-testid="inspection-documents-empty">
              <FileText className="h-9 w-9 text-[var(--text-muted)] mx-auto mb-3" />
              <p className="font-semibold text-[var(--text-primary)]">{t("management.inspectionEmptyTitle")}</p>
              <p className="text-sm text-[var(--text-muted)] mt-1">{t("management.inspectionEmptyBody")}</p>
            </div>
          ) : (
            <>
              <div className="grid gap-3 p-4 md:hidden" data-testid="inspection-documents-mobile-list">
                {documents.map((doc) => {
                  const kind = kindByValue[doc.kind];
                  const state = inspectionExpiryState(doc.expiry_date, t);
                  return (
                    <Card key={`mobile-${doc.id}`} className="border-[var(--subtle)]" data-testid={`inspection-mobile-document-${doc.id}`}>
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-semibold text-[var(--text-primary)]">{kindLabel(kind) || doc.kind}</p>
                            <p className="font-mono-num text-xs text-[var(--text-muted)] mt-1">{doc.number || "—"}</p>
                          </div>
                          <Badge className={state.className}>{state.label}</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-3 border-y border-[var(--subtle)] py-3">
                          <div>
                            <p className="text-[10px] uppercase text-[var(--text-muted)]">{t("management.inspectionIssueDate")}</p>
                            <p className="font-mono-num text-xs mt-1">{formatDate(doc.issue_date)}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase text-[var(--text-muted)]">{kind?.metadata?.date_label === "due_date" ? t("management.inspectionChequeDueDate") : t("management.inspectionExpiryDate")}</p>
                            <p className="font-mono-num text-xs mt-1">{formatDate(doc.expiry_date)}</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap justify-end gap-1">
                          {fileIdsFor(doc).map((_id, index) => (
                            <React.Fragment key={`mobile-${doc.id}-${index}`}>
                              <Button type="button" variant="outline" size="sm" onClick={() => getFile(doc, index, false)} data-testid={`inspection-mobile-view-${doc.id}-${index}`}><Eye className="h-4 w-4 me-1" />{t("common.view")}</Button>
                              <Button type="button" variant="outline" size="sm" onClick={() => getFile(doc, index, true)} data-testid={`inspection-mobile-download-${doc.id}-${index}`}><Download className="h-4 w-4 me-1" />{t("common.download")}</Button>
                            </React.Fragment>
                          ))}
                          <Button type="button" variant="outline" size="sm" onClick={() => openEdit(doc)} data-testid={`inspection-mobile-edit-${doc.id}`}><FilePenLine className="h-4 w-4 me-1" />{t("common.edit")}</Button>
                          <Button type="button" variant="outline" size="sm" onClick={() => deleteMutation.mutate(doc.id)} data-testid={`inspection-mobile-delete-${doc.id}`}><Trash2 className="h-4 w-4 me-1 text-[var(--error)]" />{t("common.delete")}</Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full min-w-[920px] text-sm grid-table">
                <thead>
                  <tr>
                    <th className="text-start px-4 py-3">{t("management.inspectionType")}</th>
                    <th className="text-start px-4 py-3">{t("management.inspectionNumber")}</th>
                    <th className="text-start px-4 py-3">{t("management.inspectionIssueDate")}</th>
                    <th className="text-start px-4 py-3">{t("management.inspectionExpiryOrDue")}</th>
                    <th className="text-start px-4 py-3">{t("management.inspectionStatus")}</th>
                    <th className="text-start px-4 py-3">{t("management.inspectionFiles")}</th>
                    <th className="text-end px-4 py-3">{t("management.inspectionActions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => {
                    const kind = kindByValue[doc.kind];
                    const state = inspectionExpiryState(doc.expiry_date, t);
                    return (
                      <tr key={doc.id} data-testid={`inspection-document-row-${doc.id}`}>
                        <td className="px-4 py-3 font-medium">{kindLabel(kind) || doc.kind}</td>
                        <td className="px-4 py-3 font-mono-num text-xs">{doc.number || "—"}</td>
                        <td className="px-4 py-3 font-mono-num text-xs">{formatDate(doc.issue_date)}</td>
                        <td className="px-4 py-3 font-mono-num text-xs">
                          <span className="block text-[10px] uppercase text-[var(--text-muted)]">{kind?.metadata?.date_label === "due_date" ? t("management.inspectionChequeDueDate") : t("management.inspectionExpiryDate")}</span>
                          {formatDate(doc.expiry_date)}
                        </td>
                        <td className="px-4 py-3"><Badge className={state.className}>{state.label}</Badge></td>
                        <td className="px-4 py-3 text-xs">{fileIdsFor(doc).length}</td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-1">
                            {fileIdsFor(doc).map((_id, index) => (
                              <React.Fragment key={`${doc.id}-${index}`}>
                                <Button type="button" variant="ghost" size="sm" onClick={() => getFile(doc, index, false)} aria-label={t("common.view")} data-testid={`inspection-view-${doc.id}-${index}`}><Eye className="h-4 w-4" /></Button>
                                <Button type="button" variant="ghost" size="sm" onClick={() => getFile(doc, index, true)} aria-label={t("common.download")} data-testid={`inspection-download-${doc.id}-${index}`}><Download className="h-4 w-4" /></Button>
                              </React.Fragment>
                            ))}
                            <Button type="button" variant="ghost" size="sm" onClick={() => openEdit(doc)} aria-label={t("common.edit")} data-testid={`inspection-edit-${doc.id}`}><FilePenLine className="h-4 w-4" /></Button>
                            <Button type="button" variant="ghost" size="sm" onClick={() => deleteMutation.mutate(doc.id)} aria-label={t("common.delete")} data-testid={`inspection-delete-${doc.id}`}><Trash2 className="h-4 w-4 text-[var(--error)]" /></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="border-[var(--warning)]/30 bg-[var(--warning-bg)]/60">
        <CardContent className="p-4 flex gap-3 text-sm">
          <CalendarClock className="h-5 w-5 text-[var(--warning)] shrink-0 mt-0.5" />
          <p className="text-[var(--text-secondary)]">{t("management.inspectionReminderNote")}</p>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={resetDialog}>
        <DialogContent data-testid="inspection-document-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? t("management.inspectionEdit") : t("management.inspectionAdd")}</DialogTitle>
            <DialogDescription>{t("management.inspectionDialogDescription")}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <Label>{t("management.inspectionType")}</Label>
              <Select value={form.kind} onValueChange={(kind) => setForm((current) => ({ ...current, kind }))}>
                <SelectTrigger className="mt-1" data-testid="inspection-document-type"><SelectValue placeholder={t("management.inspectionSelectType")} /></SelectTrigger>
                <SelectContent>
                  {documentKinds.map((row) => <SelectItem key={row.value} value={row.value}>{kindLabel(row)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="inspection-document-file">{editing ? t("management.inspectionReplacementFile") : t("management.inspectionFile")}</Label>
              <Input id="inspection-document-file" type="file" accept="application/pdf,image/jpeg,image/png" className="mt-1" onChange={(event) => setForm((current) => ({ ...current, file: event.target.files?.[0] || null }))} data-testid="inspection-document-file" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="inspection-document-number">{isCheque ? t("management.inspectionChequeNumber") : t("management.inspectionNumber")}</Label>
                <Input id="inspection-document-number" value={form.number} onChange={(event) => setForm((current) => ({ ...current, number: event.target.value }))} className="mt-1" data-testid="inspection-document-number" />
              </div>
              <div>
                <Label htmlFor="inspection-document-issue-date">{t("management.inspectionIssueDate")}</Label>
                <Input id="inspection-document-issue-date" type="date" value={form.issue_date} onChange={(event) => setForm((current) => ({ ...current, issue_date: event.target.value }))} className="mt-1" data-testid="inspection-document-issue-date" />
              </div>
            </div>
            <div>
              <Label htmlFor="inspection-document-expiry-date">{isCheque ? t("management.inspectionChequeDueDate") : t("management.inspectionExpiryDate")}</Label>
              <Input id="inspection-document-expiry-date" type="date" value={form.expiry_date} onChange={(event) => setForm((current) => ({ ...current, expiry_date: event.target.value }))} className="mt-1" data-testid="inspection-document-expiry-date" />
            </div>
            <div>
              <Label htmlFor="inspection-document-notes">{t("management.inspectionNotes")}</Label>
              <Input id="inspection-document-notes" value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} className="mt-1" data-testid="inspection-document-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => resetDialog(false)} data-testid="inspection-document-cancel">{t("common.cancel")}</Button>
            <Button type="button" onClick={() => saveMutation.mutate()} disabled={!form.kind || (!editing && !form.file) || saveMutation.isPending} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white" data-testid="inspection-document-save">
              {saveMutation.isPending ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
